import RadioInput from 'components/forms/inputs/rhf-inputs/RadioInput';
import InfoTooltip from 'components/InfoTooltip';
import { useId, useMemo } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { CaseClosingExperienceRecordDto, CloseCaseXrChoice } from 'types/caseClosing';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { CloseCaseFormValues } from '../closeCaseModalState';
import { experienceRecordUrl } from './closingLinks';
import { CollapsibleLine, PlainLine } from './CollapsibleLine';
import { NewTabLink } from './WarningRow';

interface XrChoiceOption {
    value: CloseCaseXrChoice;
    label: string;
    tooltip: JSX.Element;
}

const XR_CHOICE_MESSAGE_IDS: Record<CloseCaseXrChoice, string> = {
    [CloseCaseXrChoice.DECIDE_LATER]: 'case-closing-modal.xr.decide-later',
    [CloseCaseXrChoice.NOT_NEEDED]: 'case-closing-modal.xr.not-needed',
    [CloseCaseXrChoice.REMIND]: 'case-closing-modal.xr.remind',
};

/** "Remind me" opens the new-task form, which only a user who may create tasks can save. */
const CREATE_TASK_PERMISSIONS = ['CREATE.TASKS'];

function xrChoiceOptions(canCreateTasks: boolean): XrChoiceOption[] {
    return Object.values(CloseCaseXrChoice).map((value) => ({
        value,
        label: t(XR_CHOICE_MESSAGE_IDS[value]),
        tooltip: (
            <InfoTooltip
                text={
                    value === CloseCaseXrChoice.REMIND && !canCreateTasks
                        ? 'case-closing-modal.xr.remind-without-permission'
                        : undefined
                }
            />
        ),
    }));
}

export function ExperienceRecordSection({
    experienceRecord,
    control,
    setValue,
}: {
    experienceRecord: CaseClosingExperienceRecordDto;
    control: Control<CloseCaseFormValues>;
    setValue: UseFormSetValue<CloseCaseFormValues>;
}) {
    const summaryId = useId();
    const canCreateTasks = useHasPermissions(CREATE_TASK_PERMISSIONS);
    const options = useMemo(() => xrChoiceOptions(canCreateTasks), [canCreateTasks]);

    if (experienceRecord.state === 'WRITTEN') {
        return (
            <PlainLine>
                <span className="flex flex-wrap gap-x-3">
                    <FormattedMessage id="case-closing-modal.xr.written" />
                    {experienceRecord.experienceRecordId != null ? (
                        <NewTabLink href={experienceRecordUrl(experienceRecord.experienceRecordId)}>
                            <FormattedMessage id="case-closing-modal.xr.open-xr" />
                        </NewTabLink>
                    ) : null}
                </span>
            </PlainLine>
        );
    }

    if (experienceRecord.state === 'EXCLUDED') {
        return (
            <PlainLine>
                <FormattedMessage id="case-closing-modal.xr.excluded" />
            </PlainLine>
        );
    }

    return (
        <CollapsibleLine
            summary={
                <span id={summaryId}>
                    <FormattedMessage id="case-closing-modal.xr.none" />
                </span>
            }
        >
            <RadioInput<CloseCaseFormValues, XrChoiceOption>
                name="xrChoice"
                control={control}
                setValue={setValue}
                ariaLabelledBy={summaryId}
                options={options}
                getOptionIdentifier="value"
                getOptionLabel="label"
                getOptionTooltip="tooltip"
                getOptionDisabled={(option) => option.value === CloseCaseXrChoice.REMIND && !canCreateTasks}
            />
            <p className="text-fg-secondary m-0 text-xs">
                <FormattedMessage id="case-closing-modal.xr.helper" />
            </p>
        </CollapsibleLine>
    );
}
